import { z } from 'zod';

import {
  NOTICE_FILL_IN_RULES,
  TEMPLATE_BODY_MAX,
  messageBodyProblems,
} from '../message-templates/schemas';

/**
 * Sending, as a BATCH you poll — not a request that finishes.
 *
 * `POST /api/events/:id/dispatch` answers 202: the work is accepted, not done.
 * The server keeps sending after the response, which is what stops a locked
 * phone stranding a half-finished send. The client watches `finished` rather
 * than deciding for itself when to stop.
 */

export const DELIVERY_STATUSES = ['queued', 'sending', 'sent', 'failed'] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

/**
 * The provider's own view, and it only ever moves FORWARD.
 *
 * Callbacks arrive out of order, so the server never lets a late `delivered`
 * overwrite a `read`. The client just renders what it is given.
 */
export const PROVIDER_STATUSES = ['accepted', 'sent', 'delivered', 'read', 'undelivered'] as const;
export type ProviderStatus = (typeof PROVIDER_STATUSES)[number];

export const deliverySchema = z
  .object({
    id: z.string(),
    /** Null for the roster report, which goes to the manager, not a staff row. */
    staff_id: z.string().nullable(),
    staff_name: z.string(),
    /** `0412 880 143`. Null for the manager, who is settings, not a staff row. */
    to_display: z.string().nullable(),
    /**
     * The text AS SENT.
     *
     * A snapshot, not a re-render: it is what this person actually received,
     * even after the shift time changed underneath it. Copying it by hand is a
     * first-class path, not a fallback — a number that is not on WhatsApp, or a
     * provider that is unverified, still has to reach a human somehow.
     */
    body: z.string(),
    channel: z.string(),
    status: z.enum(DELIVERY_STATUSES),
    attempts: z.number().int().nonnegative(),
    /** Null unless a retry is scheduled. Shown so a wait reads as progress. */
    next_attempt_at: z.iso.datetime().nullable(),
    sent_at: z.iso.datetime().nullable(),
    provider_status: z.enum(PROVIDER_STATUSES).nullable(),
    delivered_at: z.iso.datetime().nullable(),
    read_at: z.iso.datetime().nullable(),
    failure_code: z.string().nullable(),
    failure_detail: z.string().nullable(),
    /**
     * Whether a Retry button should be shown AT ALL.
     *
     * The SERVER decides, and the client must not re-derive it from the failure
     * code. Getting it wrong means a coordinator tapping Retry four times
     * against a number that is not on WhatsApp and concluding the app is broken.
     */
    can_retry: z.boolean(),
  })
  .transform((raw) => ({
    id: raw.id,
    staffId: raw.staff_id,
    staffName: raw.staff_name,
    toDisplay: raw.to_display,
    body: raw.body,
    channel: raw.channel,
    status: raw.status,
    attempts: raw.attempts,
    nextAttemptAt: raw.next_attempt_at,
    sentAt: raw.sent_at,
    providerStatus: raw.provider_status,
    deliveredAt: raw.delivered_at,
    readAt: raw.read_at,
    failureCode: raw.failure_code,
    failureDetail: raw.failure_detail,
    canRetry: raw.can_retry,
  }));

export type Delivery = z.infer<typeof deliverySchema>;

/**
 * Why messages to staff are waiting. Null while they are simply going out.
 *
 * `sender_down`  — the WhatsApp number is disconnected or restricted
 * `daily_limit`  — the gateway's per-day cap, until it resets
 * `quiet_hours`  — held overnight, when quiet hours are set (off by default)
 * `gap`          — between two messages, which go 20–60 s apart
 */
export const WAITING_REASONS = ['sender_down', 'daily_limit', 'quiet_hours', 'gap'] as const;
export type WaitingReason = (typeof WAITING_REASONS)[number];

const pacingSchema = z
  .object({
    /** `paced`: messages to staff, one at a time. `immediate`: the manager's report. */
    lane: z.enum(['paced', 'immediate']),
    waiting_reason: z.enum(WAITING_REASONS).nullable(),
    next_send_at: z.iso.datetime().nullable(),
    /** An estimate — the real gaps are random. */
    estimated_finish_at: z.iso.datetime().nullable(),
  })
  .transform((raw) => ({
    lane: raw.lane,
    waitingReason: raw.waiting_reason,
    nextSendAt: raw.next_send_at,
    estimatedFinishAt: raw.estimated_finish_at,
  }));

export type BatchPacing = z.infer<typeof pacingSchema>;

export const batchSchema = z
  .object({
    id: z.string(),
    /** Set only when the batch covers exactly ONE event. See `event_ids`. */
    event_id: z.string().nullable(),
    /**
     * Every event this batch covered, in dispatch order.
     *
     * The authoritative answer. `event_id` is null for a multi-event send
     * rather than naming one of several, which would be a lie the client could
     * not detect.
     */
    event_ids: z.array(z.string()),
    kind: z.string(),
    created_at: z.iso.datetime(),
    /** Terminal for the whole batch: nothing queued, nothing sending. */
    finished: z.boolean(),
    counts: z.object({
      total: z.number().int().nonnegative(),
      queued: z.number().int().nonnegative(),
      sending: z.number().int().nonnegative(),
      sent: z.number().int().nonnegative(),
      failed: z.number().int().nonnegative(),
    }),
    deliveries: z.array(deliverySchema),
    /**
     * Where the batch stands in the send schedule. OPTIONAL so an app build
     * talking to an API from before pacing still parses a batch.
     */
    pacing: pacingSchema.optional(),
    /**
     * People deliberately skipped because they opted out.
     *
     * Present only on the response that CREATES a batch — a poll has nothing to
     * say about people who were never in it. Optional rather than defaulted so
     * "none skipped" and "not applicable" stay distinguishable.
     */
    skipped_opted_out: z.array(z.string()).optional(),
  })
  .transform((raw) => ({
    id: raw.id,
    eventId: raw.event_id,
    eventIds: raw.event_ids,
    kind: raw.kind,
    createdAt: raw.created_at,
    finished: raw.finished,
    counts: raw.counts,
    deliveries: raw.deliveries,
    pacing: raw.pacing,
    skippedOptedOut: raw.skipped_opted_out,
  }));

export type Batch = z.infer<typeof batchSchema>;

/**
 * `GET /api/messaging/health` — whether the WhatsApp number can send.
 *
 * `untracked` is a provider that cannot say (Meta's API); it can always send,
 * as far as anybody knows. Every other state but `ready` means sending is
 * PAUSED — messages wait in the queue, they are not failed.
 */
export const SENDER_STATES = [
  'ready',
  'starting',
  'needs_qr',
  'disconnected',
  'needs_action',
  'failed',
  'unreachable',
  'unknown',
  'untracked',
] as const;
export type SenderState = (typeof SENDER_STATES)[number];

export const senderHealthSchema = z
  .object({
    // Lenient: a state this build has never heard of reads as `unknown`.
    state: z.enum(SENDER_STATES).catch('unknown'),
    can_send: z.boolean(),
    restriction: z
      .object({
        kind: z.string(),
        code: z.string().nullable(),
        expires_at: z.iso.datetime().nullable(),
      })
      .nullable(),
    phone: z.string().nullable(),
    detail: z.string().nullable(),
    checked_at: z.iso.datetime(),
    queue: z.object({
      queued: z.number().int().nonnegative(),
      waiting_reason: z.enum(WAITING_REASONS).nullable(),
      next_send_at: z.iso.datetime().nullable(),
    }),
  })
  .transform((raw) => ({
    state: raw.state,
    canSend: raw.can_send,
    restriction: raw.restriction
      ? {
          kind: raw.restriction.kind,
          code: raw.restriction.code,
          expiresAt: raw.restriction.expires_at,
        }
      : null,
    phone: raw.phone,
    detail: raw.detail,
    checkedAt: raw.checked_at,
    queue: {
      queued: raw.queue.queued,
      waitingReason: raw.queue.waiting_reason,
      nextSendAt: raw.queue.next_send_at,
    },
  }));

export type SenderHealth = z.infer<typeof senderHealthSchema>;

export const QUEUED_MESSAGE_KINDS = [
  'shift_request',
  'multi_shift_request',
  'roster_report',
  'event_notice',
] as const;
export type QueuedMessageKind = (typeof QUEUED_MESSAGE_KINDS)[number];

/** One waiting message: who it is to, what it is about, when it may go. */
export const queuedMessageSchema = z
  .object({
    id: z.string(),
    batch_id: z.string(),
    // Lenient, like `state` above: a kind this build does not know is null.
    kind: z.enum(QUEUED_MESSAGE_KINDS).nullable().catch(null),
    staff_id: z.string().nullable(),
    /** Null for the roster report, which goes to the manager. */
    staff_name: z.string().nullable(),
    /** `date` is `YYYY-MM-DD`, or null where the message does not carry one. */
    events: z.array(z.object({ name: z.string(), date: z.string().nullable() })),
    attempts: z.number().int().nonnegative(),
    /** A scheduled retry or hold; null means it goes when its turn comes. */
    due_at: z.iso.datetime().nullable(),
    queued_at: z.iso.datetime(),
  })
  .transform((raw) => ({
    id: raw.id,
    batchId: raw.batch_id,
    kind: raw.kind,
    staffId: raw.staff_id,
    staffName: raw.staff_name,
    events: raw.events,
    attempts: raw.attempts,
    dueAt: raw.due_at,
    queuedAt: raw.queued_at,
  }));

export type QueuedMessage = z.infer<typeof queuedMessageSchema>;

/**
 * `GET /api/messaging/queue` — every waiting message, in the order it will go,
 * and why the queue waits. `can_send_now` is the server's call on whether Send
 * now can do anything; the client does not re-derive it.
 */
export const messageQueueSchema = z
  .object({
    items: z.array(queuedMessageSchema),
    waiting_reason: z.enum(WAITING_REASONS).nullable(),
    next_send_at: z.iso.datetime().nullable(),
    /** An estimate — the real gaps are random. */
    estimated_finish_at: z.iso.datetime().nullable(),
    can_send_now: z.boolean(),
  })
  .transform((raw) => ({
    items: raw.items,
    waitingReason: raw.waiting_reason,
    nextSendAt: raw.next_send_at,
    estimatedFinishAt: raw.estimated_finish_at,
    canSendNow: raw.can_send_now,
  }));

export type MessageQueue = z.infer<typeof messageQueueSchema>;

/**
 * Australian and Nepali mobiles, as the form accepts them — the same generous
 * pattern as the other phone fields, and deliberately DUPLICATED from the
 * server's `normaliseMobile` (see the boundary note in AGENTS.md). The server
 * normalises; this only stops a typo at the form.
 */
const MOBILE =
  /^(?:(?:\+?61\s?|0)?4\d{2}\s?\d{3}\s?\d{3}|(?:\+?977[\s-]?)?9[678]\d[\s-]?\d{3}[\s-]?\d{4})$/;

/** `POST /api/messaging/reconnect` — the WhatsApp number to link again. */
export const reconnectInputSchema = z.object({
  phone: z.string().trim().regex(MOBILE, {
    message: 'Enter an Australian or Nepali mobile — +61 4XX XXX XXX or +977 98X XXX XXXX.',
  }),
});

export type ReconnectInput = z.input<typeof reconnectInputSchema>;

/**
 * One step of reconnecting. `starting`: ask again in a few seconds. `code`:
 * the 8 characters to enter on the phone. `connected`: back, no code needed.
 */
export const RECONNECT_STATUSES = ['starting', 'code', 'connected'] as const;

export const reconnectResultSchema = z.object({
  status: z.enum(RECONNECT_STATUSES),
  code: z.string().nullable(),
});

export type ReconnectResult = z.infer<typeof reconnectResultSchema>;

/**
 * One person's message, rendered but not sent.
 *
 * For copying and pasting by hand — which is a first-class path, not a
 * fallback: a provider that is unverified or a number that is not on WhatsApp
 * still has to reach a human somehow.
 */
export const previewMessageSchema = z
  .object({
    staff_id: z.string().nullable(),
    staff_name: z.string(),
    /** `0412 880 143` — where to paste it. */
    to_display: z.string().nullable(),
    /** The exact text the provider would have been handed, link and all. */
    body: z.string(),
  })
  .transform((raw) => ({
    staffId: raw.staff_id,
    staffName: raw.staff_name,
    toDisplay: raw.to_display,
    body: raw.body,
  }));

export type PreviewMessage = z.infer<typeof previewMessageSchema>;

/**
 * `GET /api/events/:id/messages/:staffId` — one person's message on an event,
 * to copy and send by hand.
 *
 * The message ALREADY composed — sent, queued or failed — and never a new one:
 * only the stored body carries the reply link that person was given. `body` is
 * null when there is none to give (never messaged, or its link replaced since),
 * and a fresh one then comes from the preview.
 */
export const staffMessageSchema = z
  .object({
    staff_id: z.string(),
    staff_name: z.string(),
    /** `0412 880 143` — where to paste it. */
    to_display: z.string().nullable(),
    body: z.string().nullable(),
    status: z.enum(DELIVERY_STATUSES).nullable(),
    sent_at: z.iso.datetime().nullable(),
  })
  .transform((raw) => ({
    staffId: raw.staff_id,
    staffName: raw.staff_name,
    toDisplay: raw.to_display,
    body: raw.body,
    status: raw.status,
    sentAt: raw.sent_at,
  }));

export type StaffMessage = z.infer<typeof staffMessageSchema>;

export const dispatchPreviewSchema = z
  .object({
    messages: z.array(previewMessageSchema),
    skipped_opted_out: z.array(z.string()).default([]),
  })
  .transform((raw) => ({
    messages: raw.messages,
    skippedOptedOut: raw.skipped_opted_out,
  }));

export type DispatchPreview = z.infer<typeof dispatchPreviewSchema>;

export const reportSchema = z
  .object({
    event_id: z.string(),
    /**
     * Composed on the SERVER so both clients read one text.
     *
     * The report is the record of what was agreed; two renderers would be two
     * slightly different records.
     */
    text: z.string(),
  })
  .transform((raw) => ({ eventId: raw.event_id, text: raw.text }));

export type Report = z.infer<typeof reportSchema>;

/**
 * The manager's PUBLIC roster report — `GET /api/public/reports/:token`.
 *
 * Structured rather than the text above, because it is a page and not a
 * WhatsApp body: the web app lays it out per shift. Live on every read, so a
 * late yes after the link was sent still shows.
 */
export const publicReportSchema = z
  .object({
    event_name: z.string(),
    event_type: z.string(),
    /** `YYYY-MM-DD`. */
    event_date: z.string(),
    event_date_label: z.string(),
    cancelled: z.boolean(),
    shifts: z.array(
      z.object({
        start_time: z.string(),
        start_time_label: z.string(),
        min_staff: z.number().int().nonnegative(),
        /**
         * Full names of who is working — everyone who said yes, even above
         * the minimum. The manager is who the roster is for.
         */
        confirmed: z.array(z.string()),
        awaiting_count: z.number().int().nonnegative(),
      }),
    ),
    rostered_count: z.number().int().nonnegative(),
    manager_name: z.string(),
  })
  .transform((raw) => ({
    eventName: raw.event_name,
    eventType: raw.event_type,
    eventDate: raw.event_date,
    eventDateLabel: raw.event_date_label,
    cancelled: raw.cancelled,
    shifts: raw.shifts.map((shift) => ({
      startTime: shift.start_time,
      startTimeLabel: shift.start_time_label,
      minStaff: shift.min_staff,
      confirmed: shift.confirmed,
      awaitingCount: shift.awaiting_count,
    })),
    rosteredCount: raw.rostered_count,
    managerName: raw.manager_name,
  }));

export type PublicReport = z.infer<typeof publicReportSchema>;

/**
 * `POST /api/events/:id/notice` — a coordinator's own message, a template or
 * free text, to the people picked on one event.
 *
 * `body` goes UNFILLED: the server fills `{name}` and the rest per person.
 * Problems land on `body` (superRefine with a path) so the form shows them
 * under the message box.
 */
export const eventNoticeInputSchema = z
  .object({
    body: z
      .string()
      .trim()
      .min(1, 'Write the message.')
      .max(TEMPLATE_BODY_MAX, `Keep the message to ${String(TEMPLATE_BODY_MAX)} characters.`),
    staffIds: z.array(z.string().min(1)).min(1, 'Pick at least one person.'),
  })
  .superRefine((value, ctx) => {
    for (const message of messageBodyProblems(value.body, NOTICE_FILL_IN_RULES)) {
      ctx.addIssue({ code: 'custom', path: ['body'], message });
    }
  });

export type EventNoticeInput = z.input<typeof eventNoticeInputSchema>;
